import React, { useState, useEffect } from "react";
import { Gift, Camera, Heart, Baby, Flower2, Church, BookOpen, X, Copy, Check, Download } from "lucide-react";
import { GIFT_CARDS, SITE_INFO } from "../mock";
import { createGiftCard, listPhotos, photoUrl } from "../api";
import { useToast } from "../hooks/use-toast";

const iconMap = { Camera, Heart, Baby, Flower2, Church, BookOpen };
// Map gift card id -> gallery category id to pick a representative cover
const cardCategoryMap = {
  shooting: "portrait",
  couple: "duo",
  grossesse: "grossesse",
  naissance: "famille",
  book: "mode",
  bapteme: "famille",
  mariage: "mariage",
};

export default function GiftCards() {
  const [selected, setSelected] = useState(null);
  const [step, setStep] = useState("select");
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    buyerName: "", buyerEmail: "", recipientName: "", recipientEmail: "",
    message: "", payment: "virement"
  });
  const [issued, setIssued] = useState(null);
  const [copied, setCopied] = useState(false);
  const [photosByCategory, setPhotosByCategory] = useState({});
  const { toast } = useToast();

  useEffect(() => {
    listPhotos().then(all => {
      const map = {};
      for (const p of all) {
        if (!map[p.category]) map[p.category] = [];
        map[p.category].push(p);
      }
      setPhotosByCategory(map);
    }).catch(() => {});
  }, []);

  // Returns best image URL for a gift card: first uploaded photo of mapped gallery category,
  // or fallback to the hardcoded image.
  const cardImage = (card) => {
    const catId = cardCategoryMap[card.id];
    const photos = photosByCategory[catId];
    if (photos && photos.length > 0) return photoUrl(photos[0].url);
    return card.image;
  };

  const openCard = (card) => {
    setSelected(card);
    setStep("form");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const card = await createGiftCard({
        prestation: selected.id,
        prestationTitle: selected.title,
        amount: selected.price,
        ...form
      });
      setIssued(card);
      setStep("success");
      toast({ title: "Carte cadeau créée !", description: `Vous recevrez un email avec le code et les instructions de paiement.` });
    } catch (err) {
      toast({ title: "Erreur", description: err.response?.data?.detail || "Impossible de créer la carte", variant: "destructive" });
    }
    setSubmitting(false);
  };

  const copyCode = () => {
    navigator.clipboard.writeText(issued.code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const close = () => {
    setSelected(null);
    setStep("select");
    setIssued(null);
    setForm({ buyerName: "", buyerEmail: "", recipientName: "", recipientEmail: "", message: "", payment: "virement" });
  };

  return (
    <div className="bg-black text-white min-h-screen pt-32 pb-24">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-16 fade-in">
          <Gift size={48} className="text-gold mx-auto mb-6" strokeWidth={1} />
          <p className="text-gold text-sm tracking-[0.3em] uppercase mb-4">Offrir</p>
          <h1 className="text-5xl md:text-6xl mb-6 section-title"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Cartes Cadeaux
          </h1>
          <div className="gold-divider mb-6" />
          <p className="text-gray-400 max-w-2xl mx-auto">
            Offrez une expérience unique à vos proches.
            <span className="text-gold block mt-2">Sans date limite d'utilisation.</span>
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {GIFT_CARDS.map((card) => {
            const Icon = iconMap[card.icon] || Camera;
            return (
              <div key={card.id} className="gift-card overflow-hidden">
                <div className="aspect-[4/3] overflow-hidden relative">
                  <img src={cardImage(card)} alt={card.title} className="w-full h-full object-cover opacity-70" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
                  <div className="absolute top-4 right-4 w-12 h-12 border border-gold flex items-center justify-center bg-black/60">
                    <Icon size={20} className="text-gold" strokeWidth={1.5} />
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-2xl mb-2"
                      style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}>
                    {card.title}
                  </h3>
                  <p className="text-gold text-3xl mb-4"
                     style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                    {card.price}€
                  </p>
                  <p className="text-gray-400 text-sm leading-relaxed mb-6 min-h-[3rem]">
                    {card.description}
                  </p>
                  <button onClick={() => openCard(card)} className="btn-gold-filled w-full justify-center">
                    Offrir cette carte
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-16 border border-gold/30 bg-zinc-950/40 p-8 text-center">
          <h3 className="text-2xl text-gold mb-3"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Comment ça marche ?
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6 text-sm text-gray-300">
            <div><span className="text-gold text-2xl block mb-2">1.</span>Choisissez la prestation</div>
            <div><span className="text-gold text-2xl block mb-2">2.</span>Recevez le code unique et le PDF</div>
            <div><span className="text-gold text-2xl block mb-2">3.</span>Le bénéficiaire utilise le code lors de sa réservation</div>
          </div>
        </div>
      </div>

      {/* Modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-zinc-950 border border-gold/40 max-w-2xl w-full my-8 relative">
            <button onClick={close} className="absolute top-4 right-4 text-white hover:text-gold z-10">
              <X size={24} />
            </button>

            {step === "form" && (
              <form onSubmit={handleSubmit} className="p-8">
                <div className="text-center mb-6">
                  <p className="text-gold text-xs tracking-widest uppercase mb-2">Commande carte cadeau</p>
                  <h3 className="text-3xl" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}>
                    {selected.title}
                  </h3>
                  <p className="text-gold text-2xl mt-2">{selected.price}€</p>
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field label="Votre nom" value={form.buyerName} onChange={(v) => setForm({...form, buyerName: v})} required />
                    <Field label="Votre email" type="email" value={form.buyerEmail} onChange={(v) => setForm({...form, buyerEmail: v})} required />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field label="Nom du bénéficiaire" value={form.recipientName} onChange={(v) => setForm({...form, recipientName: v})} required />
                    <Field label="Email du bénéficiaire" type="email" value={form.recipientEmail} onChange={(v) => setForm({...form, recipientEmail: v})} required />
                  </div>
                  <div>
                    <label className="block text-xs tracking-wider uppercase text-gray-400 mb-2">Message personnalisé (optionnel)</label>
                    <textarea
                      value={form.message}
                      onChange={(e) => setForm({...form, message: e.target.value})}
                      rows={3}
                      className="w-full bg-black border border-white/20 focus:border-gold focus:outline-none p-3 text-white"
                      placeholder="Joyeux anniversaire !..."
                    />
                  </div>

                  <div>
                    <label className="block text-xs tracking-wider uppercase text-gray-400 mb-2">Mode de paiement *</label>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { v: "virement", l: "Virement bancaire" },
                        { v: "wero", l: "Wero" }
                      ].map(p => (
                        <button
                          key={p.v}
                          type="button"
                          data-testid={`gift-payment-${p.v}-btn`}
                          onClick={() => setForm({...form, payment: p.v})}
                          className={`py-3 px-3 text-xs uppercase tracking-wider border transition-colors ${
                            form.payment === p.v ? "border-gold bg-gold text-black" : "border-white/20 text-white/70 hover:border-gold"
                          }`}
                        >
                          {p.l}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-2">Les coordonnées (IBAN ou n° Wero) vous seront envoyées par email sécurisé.</p>
                  </div>
                </div>

                <button type="submit" disabled={submitting} className="btn-gold-filled w-full justify-center mt-6 disabled:opacity-50">
                  {submitting ? "Envoi en cours..." : "Commander la carte cadeau"}
                </button>
              </form>
            )}

            {step === "success" && issued && (
              <div className="p-8">
                <div className="text-center mb-8">
                  <div className="w-16 h-16 rounded-full border-2 border-gold mx-auto mb-4 flex items-center justify-center">
                    <Check size={32} className="text-gold" />
                  </div>
                  <h3 className="text-3xl mb-2" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}>
                    Commande enregistrée !
                  </h3>
                  <p className="text-gray-400 text-sm">Un email vient de vous être envoyé à <strong className="text-gold">{issued.buyerEmail}</strong></p>
                </div>

                {/* Gift card preview - CODE HIDDEN until paid */}
                <div className="border-2 border-gold bg-gradient-to-br from-zinc-900 to-black p-6 mb-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <p className="text-gold text-xs tracking-widest uppercase">Carte Cadeau</p>
                      <p className="text-white italic text-2xl mt-1" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                        Garcia Ludovic
                      </p>
                      <p className="text-gray-400 text-xs uppercase tracking-wider">Photographe</p>
                    </div>
                    <div className="text-right">
                      <p className="text-gold text-4xl" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}>
                        {issued.amount}€
                      </p>
                    </div>
                  </div>
                  <p className="text-white text-lg mb-4"
                     style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic" }}>
                    {issued.prestationTitle}
                  </p>
                  <div className="border-t border-gold/30 pt-3">
                    <p className="text-gray-400 text-xs uppercase tracking-wider mb-1">Statut</p>
                    <div className="flex items-center gap-2">
                      <span className="inline-block w-2 h-2 rounded-full bg-orange-400 animate-pulse" />
                      <p className="text-orange-300 font-mono text-sm tracking-wider">En attente de paiement</p>
                    </div>
                  </div>
                  <p className="text-gray-500 text-xs mt-3 italic">Pour : {issued.recipientName}</p>
                </div>

                {/* Payment instructions - SECURED: no bank details shown, no code shown */}
                <div className="border border-gold/30 bg-black p-5 mb-6">
                  <div className="flex items-start gap-3 mb-3">
                    <Check size={18} className="text-gold shrink-0 mt-0.5" />
                    <p className="text-gold text-xs uppercase tracking-widest">Prochaine étape</p>
                  </div>
                  <p className="text-gray-200 text-sm leading-relaxed mb-2">
                    <strong className="text-white">Consultez votre email</strong> à <strong className="text-gold">{issued.buyerEmail}</strong> pour :
                  </p>
                  <ul className="text-gray-300 text-sm space-y-1 ml-1 mb-3">
                    <li>• Recevoir les <strong className="text-gold">coordonnées de paiement sécurisées</strong> ({issued.payment.toUpperCase()})</li>
                    <li>• Régler le montant de <strong className="text-gold">{issued.amount}€</strong></li>
                  </ul>
                  <div className="pt-3 border-t border-white/10">
                    <p className="text-orange-300 text-sm">
                      ⚠️ <strong>Le code de la carte cadeau sera envoyé à {issued.recipientName} uniquement après réception du paiement.</strong>
                    </p>
                  </div>
                  <div className="pt-3 border-t border-white/10 mt-3 flex items-start gap-2">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-gold shrink-0 mt-0.5">
                      <circle cx="12" cy="12" r="10"/>
                      <line x1="12" y1="8" x2="12" y2="12"/>
                      <line x1="12" y1="16" x2="12.01" y2="16"/>
                    </svg>
                    <p className="text-gray-400 text-xs italic">
                      Par mesure de sécurité, les coordonnées bancaires ne sont jamais affichées sur le site. Elles vous sont envoyées en privé.
                    </p>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <button onClick={close} className="btn-gold-filled flex-1 justify-center">
                    Terminer
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, type = "text", value, onChange, required }) {
  return (
    <div>
      <label className="block text-xs tracking-wider uppercase text-gray-400 mb-2">{label}{required && " *"}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        className="w-full bg-black border border-white/20 focus:border-gold focus:outline-none p-3 text-white"
      />
    </div>
  );
}
